import React, { useEffect, useRef, memo } from 'react'
import PropTypes from 'prop-types'

const MODEL_YAW_OFFSET_DEG = 0 // adjust if your GLB is rotated sideways

const SimpleCesiumFallback = memo(({ trucks = [], imageryId, terrainId }) => {
  const cesiumContainer = useRef(null)
  const viewerRef = useRef(null)
  const hasInitialCameraSet = useRef(false)
  const truckIdsRef = useRef(new Set())

  // === Initialize Cesium Viewer ONCE ===
  useEffect(() => {
    if (!cesiumContainer.current || viewerRef.current || !window.Cesium) return
    const Cesium = window.Cesium

    const ionToken = process.env.api_endpoint_token || process.env.REACT_APP_CESIUM_ION_TOKEN
    if (!ionToken) {
      console.warn('Cesium ion token missing')
    } else {
      Cesium.Ion.defaultAccessToken = ionToken
    }

    // --- Terrain setup ---
    const terrainProvider = terrainId
      ? new Cesium.CesiumTerrainProvider({ url: Cesium.IonResource.fromAssetId(Number(terrainId)) })
      : Cesium.createWorldTerrain()

    // --- Base world imagery ---
    const baseImagery = Cesium.createWorldImagery({
      style: Cesium.IonWorldImageryStyle.AERIAL_WITH_LABELS
    })

    // --- Initialize viewer ---
    const v = new Cesium.Viewer(cesiumContainer.current, {
      animation: false,
      timeline: false,
      fullscreenButton: false,
      vrButton: false,
      geocoder: false,
      homeButton: true,
      sceneModePicker: false,
      baseLayerPicker: false,
      navigationHelpButton: false,
      infoBox: true,
      selectionIndicator: false,
      imageryProvider: baseImagery, // base world layer
      terrainProvider,
      shouldAnimate: false
    })

    v.scene.globe.depthTestAgainstTerrain = true
    v.scene.globe.enableLighting = true
    v.shadows = true
    v.scene.shadowMap.enabled = true
    v.clock.shouldAnimate = false

    // --- Add custom local imagery as overlay (optional) ---
    if (imageryId) {
      try {
        const customImagery = new Cesium.IonImageryProvider({
          assetId: Number(imageryId)
        })
        const layer = v.imageryLayers.addImageryProvider(customImagery)
        layer.alpha = 1.0 // full opacity
        layer.brightness = 1.0
      } catch (err) {
        console.error('Error loading custom imagery:', err)
      }
    }

    viewerRef.current = v

    return () => {
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy()
        viewerRef.current = null
      }
    }
  }, [imageryId, terrainId])

  // === One-time camera setup ===
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || hasInitialCameraSet.current || !window.Cesium || trucks.length === 0) return
    const Cesium = window.Cesium

    const t0 = trucks.find(
      t =>
        Number.isFinite(t.lon) && Number.isFinite(t.lat) && t.lon >= -180 && t.lon <= 180 && t.lat >= -90 && t.lat <= 90
    )

    if (t0) {
      viewer.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(t0.lon, t0.lat, 3000),
        orientation: {
          heading: Cesium.Math.toRadians(0),
          pitch: Cesium.Math.toRadians(-30),
          roll: 0
        }
      })
      hasInitialCameraSet.current = true
    }
  }, [trucks.length])

  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || !window.Cesium) return
    const Cesium = window.Cesium

    const arcgisUrl =
      'https://services7.arcgis.com/t9kIUaCUxEhJuMHm/arcgis/rest/services/Clusters_C03/FeatureServer/0/query'

    const params = new URLSearchParams({
      where: '1=1',
      outFields: '*',
      returnGeometry: 'true',
      f: 'json'
    })

    fetch(`${arcgisUrl}?${params.toString()}`)
      .then(res => res.json())
      .then(data => {
        const valid = data.features.filter(f => f.geometry && Array.isArray(f.geometry.rings))

        const geojson = {
          type: 'FeatureCollection',
          features: valid.map(f => ({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: f.geometry.rings
            },
            properties: f.attributes
          }))
        }

        Cesium.GeoJsonDataSource.load(geojson, {
          clampToGround: true,
          stroke: Cesium.Color.YELLOW,
          fill: Cesium.Color.YELLOW.withAlpha(0.4)
        }).then(ds => {
          viewer.dataSources.add(ds)

          // ✅ Apply custom color logic here
          ds.entities.values.forEach(entity => {
            const clusterId = entity.properties.Cluster_ID?.getValue()
            // Example 1: color by Cluster_ID (categorical)
            const color = Cesium.Color.fromHsl((clusterId % 20) / 20, 0.6, 0.5, 0.6)
            entity.polygon.material = color
            entity.polygon.outline = true
            entity.polygon.outlineColor = Cesium.Color.BLACK
          })

          // ✅ Move camera after styling
          viewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(-69.87, -22.63, 3000)
          })
        })
      })
      .catch(err => console.error('Failed to load FeatureServer:', err))
  }, [])

  // === Add / Update trucks ===
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || !window.Cesium) return
    const Cesium = window.Cesium
    const seen = new Set()

    const isValidLonLat = (lon, lat) =>
      Number.isFinite(lon) && Number.isFinite(lat) && lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90

    trucks.forEach(truck => {
      if (!isValidLonLat(truck.lon, truck.lat)) return
      seen.add(truck.id)

      let entity = viewer.entities.getById(truck.id)
      const position = Cesium.Cartesian3.fromDegrees(truck.lon, truck.lat, truck.altitude)
      const orientation = Cesium.Transforms.headingPitchRollQuaternion(
        position,
        new Cesium.HeadingPitchRoll(Cesium.Math.toRadians(truck.heading + MODEL_YAW_OFFSET_DEG), 0, 0)
      )

      // ✅ Choose correct model by equipment type
      let modelUri = '/models/haul_truck.glb'
      let modelScale = 0.1

      if (truck.type?.toLowerCase() === 'shovel') {
        modelUri = '/models/shovel.glb'
        modelScale = 4 // increase scale (3x larger)
      }

      if (!entity) {
        entity = viewer.entities.add({
          id: truck.id,
          name: truck.name,
          position,
          orientation,
          model: {
            uri: modelUri,
            minimumPixelSize: 64,
            maximumScale: 100,
            scale: modelScale,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
          },
          description: `
            <div>
              <strong>${truck.name}</strong><br/>
              Driver: ${truck.driver}<br/>
              Speed: ${truck.speed} km/h<br/>
              Status: ${truck.status}<br/>
              Type: ${truck.type}<br/>
              Shift: ${truck.model}
            </div>
          `
        })
        truckIdsRef.current.add(truck.id)
      } else {
        entity.position = position
        entity.orientation = orientation

        // ✅ Update model if equipment type changed
        const currentUri = entity.model.uri.getValue()
        if (currentUri !== modelUri) {
          entity.model.uri = modelUri
        }

        entity.show = true
      }
    })

    // Hide trucks no longer present
    for (const id of Array.from(truckIdsRef.current)) {
      if (!seen.has(id)) {
        const e = viewer.entities.getById(id)
        if (e) e.show = false
      }
    }
  }, [trucks])

  return <div ref={cesiumContainer} style={{ width: '100%', height: '600px', border: '1px solid #ccc' }} />
})

SimpleCesiumFallback.propTypes = {
  trucks: PropTypes.arrayOf(PropTypes.object),
  imageryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  terrainId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default SimpleCesiumFallback
